import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
} from '@chakra-ui/react';
import { useState } from 'react';

import type { MultiInstanceMeta } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  type FilterId,
  FilterType,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { isPrimaryFilterId } from '../utils/common';
import { canFilterBeSegmented } from '../utils/filterSegmentation';
import { FilterFormRender } from './filterForms/FilterFormRender';
import { PrimaryFilterFormRender } from './filterForms/PrimaryFilterFormRender';
import { SegmentedFilterFormRender } from './filterForms/SegmentedFilterFormRender';

interface FilterModalProps extends Omit<ModalProps, 'children'> {
  title?: string;
  filter: Filter | null;
  selectedMultiMeta?: MultiInstanceMeta;
  selectedFilterId?: FilterId;
  groupKeyOverride?: string;
  disabledLevels?: number[];
  selectionLimit?: number;
  currentTreeSelections?: TreeSelection[];
  showSelectedValues?: boolean;
  allowMultiLevelSelect?: boolean;
}

const isTreeFilter = (filter: Filter | null): filter is TreeFilter => {
  return filter?.type === FilterType.TREE;
};

/**
 * FilterModal
 *
 * The <FilterModal> component is a modal version of the <FilterPopover> component.
 */
export const FilterModal = ({
  isOpen,
  onClose,
  title,
  filter,
  selectedMultiMeta,
  selectedFilterId,
  groupKeyOverride,
  disabledLevels,
  selectionLimit,
  currentTreeSelections,
  showSelectedValues: showSelectedValuesProp,
  allowMultiLevelSelect,
  ...modalProps
}: FilterModalProps) => {
  const [segmentStep, setSegmentStep] = useState<number>(0);

  const showSelectedValues =
    showSelectedValuesProp &&
    selectionLimit !== undefined &&
    selectionLimit > 1;

  const handleSegmentBack = () => {
    if (segmentStep === 2) {
      setSegmentStep(1);
    } else {
      setSegmentStep(0);
    }
  };

  const showPrimaryForm =
    filter?.isPrimary &&
    isPrimaryFilterId(selectedFilterId) &&
    isTreeFilter(filter);
  const showSegmentedForm =
    !showPrimaryForm && filter && canFilterBeSegmented(filter);
  const showBasicForm = !showPrimaryForm && !showSegmentedForm;

  return (
    <Modal isOpen={isOpen} onClose={onClose} {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        {title && (
          <ModalHeader pb={0} color="text.primary" fontSize="18px">
            {title}
            {/* <Text as="span" ml="2" fontSize="14px" color="gray.600">
              ({currentTreeSelections?.length ?? 0}/{selectionLimit} Selected)
            </Text> */}
          </ModalHeader>
        )}
        <ModalBody>
          {showPrimaryForm && (
            <PrimaryFilterFormRender
              close={onClose}
              selectedFilterId={selectedFilterId}
              primaryFilter={filter}
              groupKeyOverride={groupKeyOverride}
              disabledLevels={disabledLevels}
              selectionLimit={selectionLimit}
              showSelectedValues={showSelectedValues}
              replaceSingleSelections={selectionLimit === 1}
              currentTreeSelections={currentTreeSelections}
              allowMultiLevelSelect={allowMultiLevelSelect}
            />
          )}
          {showSegmentedForm && (
            <SegmentedFilterFormRender
              filter={filter}
              close={onClose}
              selectedMultiMeta={selectedMultiMeta}
              selectedFilterId={selectedFilterId}
              segmentStep={segmentStep}
              setSegmentStep={setSegmentStep}
              handleSegmentBack={handleSegmentBack}
            />
          )}
          {showBasicForm && (
            <FilterFormRender
              filter={filter}
              close={onClose}
              selectedMultiMeta={selectedMultiMeta}
              selectedFilterId={selectedFilterId}
              groupKeyOverride={groupKeyOverride}
              disabledLevels={disabledLevels}
              selectionLimit={selectionLimit}
              showSelectedValues={showSelectedValues}
              replaceSingleSelections={selectionLimit === 1}
              currentTreeSelections={currentTreeSelections}
              allowMultiLevelSelect={allowMultiLevelSelect}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
